import {
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { ErrorLabel, Wrapper } from '../../../components';
import {
    Button,
    Checkbox,
    CheckboxList,
    RecaptchaDisclaimer,
    SelectInput,
    Textarea,
    TextInput,
} from '../../../compositions';
import { FormOption, FormProps } from '../../../entities/@forms/Form/Form';
import {
    assortmentLocationOptions,
    assortmentSquareMeterOptions,
    participantHistoryOptions,
    ParticipateFormData,
} from '../../../entities/@forms/ParticipateForm/ParticipateForm';
import { countryOptions, defaultCountry } from '../../../entities/Country/Country';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { DialogWithButton } from '../../DialogWithButton/DialogWithButton';
import { ParticipateFormErrors, validateParticipateFormData } from './validations';

import './ParticipateForm.scss';

interface ParticipateFormProps extends FormProps<ParticipateFormData> {
    termsAndConditions?: string;
    className?: string;
}

export const ParticipateForm: FC<ParticipateFormProps> = ({
    isLoading,
    onSubmit,
    termsAndConditions,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formRef = useRef<HTMLFormElement>(null);
    const [formErrors, setFormErrors] = useState<ParticipateFormErrors>({});
    const [botDetected, setBotDetected] = useState<boolean>(false);

    const defaultCountryOption = countryOptions.find(item => item.value === defaultCountry) || countryOptions[0];

    const [companyName, setCompanyName] = useState<string>('');
    const [chamberOfCommerce, setChamberOfCommerce] = useState<string>('');
    const [postalCode, setPostalCode] = useState<string>('');
    const [houseNumber, setHouseNumber] = useState<string>('');
    const [addition, setAddition] = useState<string>('');
    const [street, setStreet] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [country, setCountry] = useState<FormOption>(defaultCountryOption);

    const [assortmentDescription, setAssortmentDescription] = useState<string>('');
    const [signupReason, setSignupReason] = useState<string>('');

    const [assortmentLocationPreference, setAssortmentLocationPreference] = useState<string[]>([]);
    const [assortmentSquareMeters, setAssortmentSquareMeters] = useState<FormOption>(assortmentSquareMeterOptions[0]);
    const [participantHistory, setParticipantHistory] = useState<FormOption>(participantHistoryOptions[0]);
    const [website, setWebsite] = useState<string>('');
    const [pointsOfSaleOffline, setPointsOfSaleOffline] = useState<string>('');
    const [pointsOfSaleOnline, setPointsOfSaleOnline] = useState<string>('');

    const [firstName, setFirstName] = useState<string>('');
    const [insertion, setInsertion] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');

    const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState<boolean>(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!executeRecaptcha) {
            setBotDetected(true);
            return;
        }

        const token = await executeRecaptcha();

        const formData: ParticipateFormData = {
            recaptcha: token,
            companyName,
            chamberOfCommerce,
            postalCode: postalCode.toUpperCase(),
            houseNumber,
            addition,
            street,
            city,
            country: country.value.toUpperCase(),
            assortmentDescription,
            signupReason,
            assortmentLocationPreference: assortmentLocationPreference.join(', '),
            assortmentSquareMeters: assortmentSquareMeters.value,
            participantHistory: participantHistory.value,
            website,
            pointsOfSaleOffline,
            pointsOfSaleOnline,
            firstName,
            insertion,
            lastName,
            email,
            phoneNumber,
            acceptTermsAndConditions,
        };

        const [errors, hasErrors] = validateParticipateFormData(formData);

        setFormErrors(errors);
        setBotDetected(!token);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
        }
    };

    const handleCountryDropDown = (value: string): void => {
        const newValue = countryOptions.find(item => item.value === value) as FormOption;
        setCountry(newValue);
    };

    const handleAssortmentSquareMetersDropDown = (value: string): void => {
        const newValue = assortmentSquareMeterOptions.find(item => item.value === value) as FormOption;
        setAssortmentSquareMeters(newValue);
    };

    const handleParticipantHistoryDropDown = (value: string): void => {
        const newValue = participantHistoryOptions.find(item => item.value === value) as FormOption;
        setParticipantHistory(newValue);
    };

    return (
        <Wrapper className={`participate-form ${className}`}>
            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="participate-form__form"
            >
                <p className="participate-form__title">
                    {trans('containers.participateForm.title')}
                </p>

                <div className="participate-form__group">
                    <TextInput
                        required
                        label={trans('form.companyName')}
                        value={companyName}
                        error={formErrors.companyName}
                        disabled={isLoading}
                        onChange={setCompanyName}
                        className="participate-form__item"
                    />

                    <TextInput
                        required
                        label={trans('form.chamberOfCommerce')}
                        value={chamberOfCommerce}
                        error={formErrors.chamberOfCommerce}
                        disabled={isLoading}
                        onChange={setChamberOfCommerce}
                        className="participate-form__item"
                    />

                    <div className="participate-form__row">
                        <TextInput
                            required
                            label={trans('form.street')}
                            value={street}
                            error={formErrors.street}
                            disabled={isLoading}
                            onChange={setStreet}
                            className="participate-form__item"
                        />

                        <TextInput
                            required
                            type="number"
                            inputMode="numeric"
                            label={trans('form.houseNumber')}
                            value={houseNumber}
                            error={formErrors.houseNumber}
                            disabled={isLoading}
                            onChange={setHouseNumber}
                            className="participate-form__item"
                        />

                        <TextInput
                            label={trans('form.addition')}
                            value={addition}
                            error={formErrors.addition}
                            disabled={isLoading}
                            onChange={setAddition}
                            className="participate-form__item"
                        />
                    </div>

                    <div className="participate-form__row participate-form__row--two">
                        <TextInput
                            required
                            label={trans('form.postalCode')}
                            value={postalCode}
                            maxLength={7}
                            error={formErrors.postalCode}
                            disabled={isLoading}
                            onChange={setPostalCode}
                            className="participate-form__item"
                            fieldClassName="participate-form__field--postal-code"
                        />

                        <TextInput
                            required
                            label={trans('form.city')}
                            value={city}
                            error={formErrors.city}
                            disabled={isLoading}
                            onChange={setCity}
                            className="participate-form__item"
                        />
                    </div>

                    <SelectInput
                        required
                        name="countrySelect"
                        options={countryOptions}
                        disabled={isLoading}
                        label={trans('form.country')}
                        placeholder={country.label}
                        value={country}
                        onChange={handleCountryDropDown}
                        className="participate-form__item"
                    />
                </div>

                <div className="participate-form__group">
                    <Textarea
                        required
                        maxLength={100}
                        label={trans('containers.participateForm.assortmentDescription')}
                        value={assortmentDescription}
                        error={formErrors.assortmentDescription}
                        disabled={isLoading}
                        onChange={setAssortmentDescription}
                        className="participate-form__item"
                        fieldClassName="participate-form__field"
                    />

                    <Textarea
                        required
                        maxLength={200}
                        label={trans('containers.participateForm.signupReason')}
                        value={signupReason}
                        error={formErrors.signupReason}
                        disabled={isLoading}
                        onChange={setSignupReason}
                        className="participate-form__item"
                        fieldClassName="participate-form__field"
                    />

                    <CheckboxList
                        required
                        label={trans('containers.participateForm.assortmentLocationPreference')}
                        name="assortmentLocationPreference"
                        value={assortmentLocationPreference}
                        error={formErrors.assortmentLocationPreference}
                        options={assortmentLocationOptions}
                        onChange={setAssortmentLocationPreference}
                        className="participate-form__item"
                    />

                    <SelectInput
                        required
                        name="assortmentSquareMeters"
                        options={assortmentSquareMeterOptions}
                        disabled={isLoading}
                        label={trans('containers.participateForm.assortmentSquareMeters')}
                        placeholder={assortmentSquareMeters.label}
                        value={assortmentSquareMeters}
                        error={formErrors.assortmentSquareMeters}
                        onChange={handleAssortmentSquareMetersDropDown}
                        className="participate-form__item"
                    />
                </div>

                <div className="participate-form__group">
                    <SelectInput
                        required
                        name="participantHistory"
                        options={participantHistoryOptions}
                        disabled={isLoading}
                        label={trans('containers.participateForm.participantHistory')}
                        placeholder={participantHistory.label}
                        value={participantHistory}
                        error={formErrors.participantHistory}
                        onChange={handleParticipantHistoryDropDown}
                        className="participate-form__item"
                    />

                    <TextInput
                        inputMode="url"
                        label={trans('form.website')}
                        value={website}
                        error={formErrors.website}
                        disabled={isLoading}
                        onChange={setWebsite}
                        className="participate-form__item"
                    />

                    <Textarea
                        maxLength={100}
                        label={trans('containers.participateForm.pointsOfSaleOffline')}
                        value={pointsOfSaleOffline}
                        error={formErrors.pointsOfSaleOffline}
                        disabled={isLoading}
                        onChange={setPointsOfSaleOffline}
                        className="participate-form__item"
                        fieldClassName="participate-form__field"
                    />

                    <Textarea
                        maxLength={100}
                        label={trans('containers.participateForm.pointsOfSaleOnline')}
                        value={pointsOfSaleOnline}
                        error={formErrors.pointsOfSaleOnline}
                        disabled={isLoading}
                        onChange={setPointsOfSaleOnline}
                        className="participate-form__item"
                        fieldClassName="participate-form__field"
                    />
                </div>

                <div className="participate-form__group">
                    <div className="participate-form__row">
                        <TextInput
                            required
                            label={trans('form.firstName')}
                            value={firstName}
                            error={formErrors.firstName}
                            disabled={isLoading}
                            onChange={setFirstName}
                            className="participate-form__item"
                        />

                        <TextInput
                            label={trans('form.insertion')}
                            value={insertion}
                            error={formErrors.insertion}
                            disabled={isLoading}
                            onChange={setInsertion}
                            className="participate-form__item"
                        />

                        <TextInput
                            required
                            label={trans('form.lastName')}
                            value={lastName}
                            error={formErrors.lastName}
                            disabled={isLoading}
                            onChange={setLastName}
                            className="participate-form__item"
                        />
                    </div>

                    <TextInput
                        required
                        type="email"
                        inputMode="email"
                        label={trans('form.email')}
                        value={email}
                        error={formErrors.email}
                        disabled={isLoading}
                        onChange={setEmail}
                        className="participate-form__item"
                    />

                    <TextInput
                        required
                        type="tel"
                        inputMode="tel"
                        label={trans('form.phoneNumber')}
                        value={phoneNumber}
                        error={formErrors.phoneNumber}
                        disabled={isLoading}
                        onChange={setPhoneNumber}
                        className="participate-form__item"
                    />
                </div>

                <Checkbox
                    required
                    label={trans('form.acceptTermsAndConditions')}
                    error={formErrors.acceptTermsAndConditions}
                    checked={acceptTermsAndConditions}
                    name="acceptTermsAndConditions"
                    disabled={isLoading}
                    onChange={setAcceptTermsAndConditions}
                    className="participate-form__item"
                />

                {termsAndConditions && (
                    <DialogWithButton
                        text={termsAndConditions}
                        buttonText={trans('containers.participateForm.readTheTermsAndConditions')}
                        title={trans('containers.participateForm.termsAndConditions')}
                    />
                )}

                <Button
                    type="submit"
                    text={trans('containers.participateForm.sendRequest')}
                    disabled={!acceptTermsAndConditions}
                    className="participate-form__button"
                />

                <RecaptchaDisclaimer className="participate-form__recaptcha-disclaimer" />

                {botDetected && (
                    <ErrorLabel text={trans('errors.unknownError')} />
                )}
            </form>
        </Wrapper>
    );
};

